import { Box, Flex, Text, useColorMode } from "@chakra-ui/react";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

export interface DoughnutSlice {
  key: string;
  label: string;
  /** An sm* semantic color token; resolved through its CSS variable so the theme stays in charge. */
  colorToken: string;
  count: number;
}

/**
 * The shared doughnut-plus-legend body of the home page's breakdown charts (agreements expiring,
 * procurements by phase): a doughnut and a clickable legend row per slice, both opening the
 * filtered list behind the slice.
 */
export const DoughnutBreakdown = ({
  slices,
  totalLabel,
  onSelect,
}: {
  slices: DoughnutSlice[];
  totalLabel: string;
  onSelect: (index: number) => void;
}) => {
  const { colorMode } = useColorMode();

  // The canvas cannot paint a var(--chakra-colors-*) reference, so resolve each token to its
  // concrete value; the chart remounts on a color-mode flip (key below) to re-resolve. Outside a
  // themed DOM (unit tests) the variable is undefined and the token string falls through.
  const resolveToken = (token: string) => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(`--chakra-colors-${token}`).trim();
    return value || token;
  };

  const total = slices.reduce((sum, slice) => sum + slice.count, 0);

  return (
    <Flex px="4" py="4" gap="5" align="center" wrap="wrap">
      <Box w="150px" h="150px" flexShrink={0}>
        <Doughnut
          key={colorMode}
          data={{
            labels: slices.map((slice) => slice.label),
            datasets: [
              {
                data: slices.map((slice) => slice.count),
                backgroundColor: slices.map((slice) => resolveToken(slice.colorToken)),
                borderWidth: 0,
              },
            ],
          }}
          options={{
            cutout: "62%",
            plugins: { legend: { display: false } },
            onClick: (_event, elements) => {
              if (elements.length > 0) onSelect(elements[0].index);
            },
          }}
        />
      </Box>
      <Box flex="1" minW="9rem">
        {slices.map((slice, index) => (
          <Flex
            as="button"
            type="button"
            key={slice.key}
            w="full"
            align="center"
            gap="2"
            py="1"
            rounded="md"
            _hover={{ bg: "smBackgroundSecondary" }}
            onClick={() => onSelect(index)}
          >
            <Box w="8px" h="8px" rounded="full" bg={slice.colorToken} flexShrink={0} />
            <Text fontSize="sm" color="smMuted" flex="1" textAlign="left" isTruncated>
              {slice.label}
            </Text>
            <Text fontSize="sm" color="smText">
              {slice.count}
            </Text>
          </Flex>
        ))}
        <Text fontSize="xs" color="smMuted" mt="1">
          {totalLabel}: {total}
        </Text>
      </Box>
    </Flex>
  );
};
